import React, { useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "organisms/iconCommonent";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { MdDelete } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";

type DataPoint = { x: number; y: number; _id?: string };
type GraphLine = {
  name: string;
  style: "solid" | "dotted" | "bar";
  color: string;
  dataPoints: DataPoint[];
  _id?: string;
};
type SubTab = {
  title: string;
  xAxisTitle: string;
  yAxisTitle: string;
  _id: string;
  graphLines: GraphLine[];
};
type Tab = { tabName: string; desc?: string; _id: string; subtabs: SubTab[] };

export default function GraphAdminPanel() {
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [selectedTab, setSelectedTab] = useState<Tab | null>(null);
  const [popup, setPopup] = useState<"tab" | "subtab" | "graphline" | null>(
    null
  );
  const [formData, setFormData] = useState<any>({});

  useEffect(() => {
    fetchTabs();
  }, []);

  const fetchTabs = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_LOCAL_URL}/api/graphData`);
      setTabs(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Tab actions
  const addTab = async () => {
    if (!formData.tabName) return;
    await axios.post(`${process.env.REACT_APP_LOCAL_URL}/api/graphData`, {
      tabName: formData.tabName,
      desc: formData.desc,
      graphdetails: formData.graphdetails || [],
    });
    fetchTabs();
    setFormData({});
    setPopup(null);
  };

  const updateTab = async (tabId: string) => {
    await axios.put(`${process.env.REACT_APP_LOCAL_URL}/api/graphData/${tabId}`, formData);
    fetchTabs();
    setPopup(null);
  };

  const deleteTab = async (tabId: string) => {
    await axios.delete(`${process.env.REACT_APP_LOCAL_URL}/api/graphData/${tabId}`);
    fetchTabs();
    setSelectedTab(null);
  };

  // Subtab actions
  const addSubtab = async () => {
    if (!selectedTab || !formData.title) return;
    await axios.post(
      `${process.env.REACT_APP_LOCAL_URL}/api/graphData/${selectedTab._id}/subtabs`,
      {
        title: formData.title,
        xAxisTitle: formData.xAxisTitle,
        yAxisTitle: formData.yAxisTitle,
      }
    );
    fetchTabs();
    setFormData({});
    setPopup(null);
  };

  const updateSubtab = async (subtabId: string) => {
    if (!selectedTab) return;
    await axios.put(
      `${process.env.REACT_APP_LOCAL_URL}/api/graphData/${selectedTab._id}/subtabs/${subtabId}`,
      formData
    );
    fetchTabs();
    setFormData({});
    setPopup(null);
  };

  const deleteSubtab = async (subtabId: string) => {
    if (!selectedTab) return;
    await axios.delete(
      `${process.env.REACT_APP_LOCAL_URL}/api/graphData/${selectedTab._id}/subtabs/${subtabId}`
    );
    fetchTabs();
  };

  // Graph line actions
  const updateGraphLine = async (subtabId: string, lineId: string) => {
    if (!selectedTab) return;
    await axios.patch(
      `${process.env.REACT_APP_LOCAL_URL}/api/graphData/${selectedTab._id}/subtabs/${subtabId}/lines/${lineId}`,
      formData
    );
    fetchTabs();
    setFormData({});
    setPopup(null);
  };

  const deleteGraphLine = async (subtabId: string, lineId: string) => {
    if (!selectedTab) return;
    await axios.delete(
      `${process.env.REACT_APP_LOCAL_URL}/api/graphData/${selectedTab._id}/subtabs/${subtabId}/lines/${lineId}`
    );
    fetchTabs();
  };

  return (
    <div className="p-6 ">
      <h1 className=" font-lato-regular">Section-04
        (Graph)
      </h1>

      {/* Tabs */}
      <div className="flex gap-8 mt-4 items-center w-full bg-white border border-gray-200 py-2 rounded-xl overflow-hidden relative">
        {tabs.map((tab) => (
          <button
            key={tab._id}
            onClick={() => setSelectedTab(tab)}
            className={`h-fit px-2 py-2 rounded text-md ${
              selectedTab?._id === tab._id
                ? "bg-pear  text-black font-lato-regular"
                : " text-black font-lato-regular "
            }`}
          >
            {tab.tabName}
          </button>
        ))}

       
      </div>

      {/* Selected Tab */}
      {selectedTab && (
        <div className="mt-4">
          <h2 className="text-xl font-semibold">{selectedTab.tabName}</h2>
          <p className="text-gray-600 py-2">{selectedTab.desc}</p>

          <div className="flex gap-4 my-2">
            <button
              onClick={() => deleteTab(selectedTab._id)}
              className="flex items-center gap-2 px-3 py-1 border border-red-400 text-red-600 rounded hover:bg-red-50 transition"
            >
              <Icon icon={MdDelete} />

              <span className="text-sm font-medium">Delete Tab</span>
            </button>
          </div>

          {/* Subtabs */}
          <div className="mt-8">
            <h2 className="text-lg font-semibold">Subtabs</h2>
            <table className="w-full mt-2 border">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Title</th>
                  <th className="p-2">X Axis</th>
                  <th className="p-2">Y Axis</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedTab.subtabs.map((subtab) => (
                  <tr key={subtab._id} className="border-b">
                    <td className="p-2">{subtab.title}</td>
                    <td className="p-2">{subtab.xAxisTitle}</td>
                    <td className="p-2">{subtab.yAxisTitle}</td>
                    <td className="p-2 flex gap-2 flex-wrap">
                      <button
                        onClick={() => {
                          setFormData(subtab);
                          setPopup("subtab");
                        }}
                        className="flex items-center gap-2 px-3 py-1 border border-blue-400 text-blue-600 rounded hover:bg-red-50 transition"
                      >
                        <Icon icon={FaRegEdit} />

                        <span className="text-sm font-medium">Edit</span>
                      </button>

                      <button
                        onClick={() => deleteSubtab(subtab._id)}
                        className="flex items-center gap-2 px-3 py-1 border border-red-400 text-red-600 rounded hover:bg-red-50 transition"
                      >
                        <Icon icon={MdDelete} />

                        <span className="text-sm font-medium">Delete</span>
                      </button>
                      {subtab.graphLines.map((line) => (
                        <button
                          key={line._id}
                          onClick={() => {
                            setFormData({
                              ...line,
                              subtabId: subtab._id,
                              xAxisTitle: subtab.xAxisTitle,
                              yAxisTitle: subtab.yAxisTitle,
                            });
                            setPopup("graphline");
                          }}
                          className="flex items-center gap-2 px-3 py-1 border border-blue-400 text-blue-600 rounded hover:bg-red-50 transition"
                        >
                            <Icon icon={FaRegEdit} />

                        <span className="text-sm font-medium">Edit Graph: {line.name}</span>
                       
                        </button>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
           
          </div>
        </div>
      )}

      {/* Popup Modal */}
      {popup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-md w-96 max-h-[90vh] overflow-y-auto">
            {/* Tab Popup */}
            {popup === "tab" && (
              <>
                <h3 className="text-lg font-semibold mb-4">Add Tab</h3>
                <input
                  type="text"
                  placeholder="Tab Name"
                  value={formData.tabName || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, tabName: e.target.value })
                  }
                  className="w-full p-2 border rounded mb-2"
                />
                <input
                  type="text"
                  placeholder="Description"
                  value={formData.desc || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, desc: e.target.value })
                  }
                  className="w-full p-2 border rounded mb-2"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setPopup(null)}
                    className="px-4 py-2 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addTab}
                    className="px-4 py-2 bg-green-500 text-black rounded"
                  >
                    Save
                  </button>
                </div>
              </>
            )}

            {/* Subtab Popup */}
            {popup === "subtab" && (
              <>
                <h3 className="text-lg font-semibold mb-4">
                  {formData._id ? "Edit Subtab" : "Add Subtab"}
                </h3>
                <input
                  type="text"
                  placeholder="Subtab Title"
                  value={formData.title || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full p-2 border rounded mb-2"
                />
                <input
                  type="text"
                  placeholder="X Axis"
                  value={formData.xAxisTitle || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, xAxisTitle: e.target.value })
                  }
                  className="w-full p-2 border rounded mb-2"
                />
                <input
                  type="text"
                  placeholder="Y Axis"
                  value={formData.yAxisTitle || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, yAxisTitle: e.target.value })
                  }
                  className="w-full p-2 border rounded mb-2"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setPopup(null)}
                    className="px-4 py-2 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() =>
                      formData._id ? updateSubtab(formData._id) : addSubtab()
                    }
                    className="px-4 py-2 bg-green-500 border text-black rounded"
                  >
                    Save
                  </button>
                </div>
              </>
            )}

            {/* Graph Line Popup */}
            {popup === "graphline" && (
              <div className="z-[999]">
                <h3 className="text-lg font-semibold mb-4">Edit Graph Line</h3>

                {/* Axis titles (read-only) */}
                <div className="mb-2">
                  <label className="block text-gray-700 font-medium">
                    X Axis
                  </label>
                  <input
                    type="text"
                    value={formData.xAxisTitle || ""}
                    disabled
                    className="w-full p-2 border rounded bg-gray-100"
                  />
                </div>
                <div className="mb-2">
                  <label className="block text-gray-700 font-medium">
                    Y Axis
                  </label>
                  <input
                    type="text"
                    value={formData.yAxisTitle || ""}
                    disabled
                    className="w-full p-2 border rounded bg-gray-100"
                  />
                </div>

                {/* Graph line details */}
                <input
                  type="text"
                  placeholder="Name"
                  value={formData.name || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full p-2 border rounded mb-2"
                />
                <input
                  type="text"
                  placeholder="Color"
                  value={formData.color || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  className="w-full p-2 border rounded mb-2"
                />
                <select
                  value={formData.style || "solid"}
                  onChange={(e) =>
                    setFormData({ ...formData, style: e.target.value })
                  }
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="solid">Solid</option>
                  <option value="dotted">Dotted</option>
                  <option value="bar">Bar</option>
                </select>

                {/* Data Points */}
                <div className="mb-2">
                  <h4 className="font-semibold mb-1">Data Points</h4>
                  {formData.dataPoints?.map(
                    (point: DataPoint, index: number) => (
                      <div key={index} className="flex gap-2 mb-1">
                        <input
                          type="number"
                          placeholder="X"
                          value={point.x}
                          onChange={(e) => {
                            const newPoints = [...formData.dataPoints];
                            newPoints[index].x = +e.target.value;
                            setFormData({ ...formData, dataPoints: newPoints });
                          }}
                          className="w-1/2 p-2 border rounded"
                        />
                        <input
                          type="number"
                          placeholder="Y"
                          value={point.y}
                          onChange={(e) => {
                            const newPoints = [...formData.dataPoints];
                            newPoints[index].y = +e.target.value;
                            setFormData({ ...formData, dataPoints: newPoints });
                          }}
                          className="w-1/2 p-2 border rounded"
                        />

                        <button
                          onClick={() => {
                            const newPoints = [...formData.dataPoints];
                            newPoints.splice(index, 1);
                            setFormData({ ...formData, dataPoints: newPoints });
                          }}
                          className="flex items-center gap-2 px-3 py-1 border border-red-400 text-red-600 rounded hover:bg-red-50 transition"
                        >
                          <Icon icon={MdDelete} />

                          <span className="text-sm font-medium">Delete </span>
                        </button>
                      </div>
                    )
                  )}
                  <button
                    onClick={() =>
                      setFormData({
                        ...formData,
                        dataPoints: [
                          ...(formData.dataPoints || []),
                          { x: 0, y: 0 },
                        ],
                      })
                    }
                    className="px-2 py-1 border text-black rounded"
                  >
                    + Add Data Point
                  </button>
                </div>

                <div className="flex justify-between gap-2 mt-2">
                  <button
                    onClick={() => setPopup(null)}
                    className="px-4 py-2 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() =>
                      updateGraphLine(formData.subtabId, formData._id)
                    }
                    className="px-4 py-2 border text-black rounded"
                  >
                    Save
                  </button>

                  <button
                    onClick={() => {
                      deleteGraphLine(formData.subtabId, formData._id);
                      setPopup(null);
                    }}
                    className="flex items-center gap-2 px-3 py-1 border border-red-400 text-red-600 rounded hover:bg-red-50 transition"
                  >
                    <Icon icon={MdDelete} />

                    <span className="text-sm font-medium">
                      Delete Graph Line
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
